import { useNavigate } from 'react-router-dom'
// eslint-disable-next-line import/no-extraneous-dependencies
import { Button, Select, Table, Tag } from 'antd'
// eslint-disable-next-line import/no-extraneous-dependencies
import type { ColumnsType } from 'antd/es/table'
import { ToastContainer } from 'react-toastify'
import { ContainerTitle, DivTitle } from '../../components/TitlePage/TitlePage'
import { Loading } from '../../components/Loading/Loading'
import DatesPicker from '../../components/Calendar/datePicker'
import { colors } from '../../theme'
import { StatusBase, useGestaoTenants } from './useGestaoTenants'
import {
  FASE_LABELS,
  FASE_OPTIONS,
  calcularDelta,
  formatData,
  formatDataHora,
  formatDelta,
  formatHealth,
  formatTaxaFalha,
} from './gestaoTenants.helpers'
import {
  CardHeader,
  CardMetrics,
  Carimbo,
  Container,
  DeltaHint,
  DesktopTabela,
  FilterBar,
  FilterField,
  HeaderBar,
  MetricItem,
  MobileCards,
  TableWrapper,
  TenantCard,
} from './GestaoTenants.style'

const STATUS_BASE_OPTIONS = [
  { value: 'ativas', label: 'Ativas' },
  { value: 'inativas', label: 'Inativas' },
  { value: 'todas', label: 'Todas' },
]

// Métricas resumidas de um tenant para o card (layout mobile). Valor pode ser número ou texto já formatado.
const cardMetrics = (
  t: TenantRankingItemType
): { label: string; value: number | string }[] => [
  { label: 'WhatsApp', value: t.atual.whatsappEnviado },
  { label: 'E-mail', value: t.atual.emailEnviado },
  { label: 'Taxa de falha', value: formatTaxaFalha(t.atual.taxaFalha) },
  { label: 'Health', value: formatHealth(t.estado.healthScore) },
  { label: 'Nº de vendas', value: t.estado.numVendaItens },
  { label: 'Usuários ativos', value: t.vinculos.usuariosAtivosPeriodo },
  { label: 'Último login', value: formatDataHora(t.vinculos.ultimoLogin) },
  { label: 'Atualizado em', value: formatDataHora(t.atualizadoEm) },
]

const celulaMetrica = (atual: number, anterior: number) => {
  const delta = calcularDelta(atual, anterior)
  return (
    <>
      <span>{atual}</span>
      {delta !== null && delta !== 0 ? (
        <DeltaHint positivo={delta > 0} negativo={delta < 0}>
          {formatDelta(delta)}
        </DeltaHint>
      ) : null}
    </>
  )
}

export function GestaoTenants() {
  const navigate = useNavigate()
  const {
    ranking,
    isLoading,
    recalculando,
    fases,
    setFases,
    statusBase,
    setStatusBase,
    onChangeDataInicio,
    onChangeDataFim,
    recalcular,
  } = useGestaoTenants()

  const tenants = ranking?.tenants ?? []

  const columns: ColumnsType<TenantRankingItemType> = [
    {
      title: 'Tenant',
      dataIndex: 'nome',
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Fase',
      dataIndex: 'fase',
      render: (fase: FaseTenant) => <Tag>{FASE_LABELS[fase]}</Tag>,
      sorter: (a, b) => FASE_LABELS[a.fase].localeCompare(FASE_LABELS[b.fase]),
    },
    {
      title: 'WhatsApp',
      render: (_, r) =>
        celulaMetrica(r.atual.whatsappEnviado, r.anterior.whatsappEnviado),
      sorter: (a, b) => a.atual.whatsappEnviado - b.atual.whatsappEnviado,
    },
    {
      title: 'E-mail',
      render: (_, r) =>
        celulaMetrica(r.atual.emailEnviado, r.anterior.emailEnviado),
      sorter: (a, b) => a.atual.emailEnviado - b.atual.emailEnviado,
    },
    {
      title: 'Taxa de falha',
      render: (_, r) => formatTaxaFalha(r.atual.taxaFalha),
      sorter: (a, b) => (a.atual.taxaFalha ?? -1) - (b.atual.taxaFalha ?? -1),
    },
    {
      title: 'Baixas',
      render: (_, r) => celulaMetrica(r.atual.numBaixas, r.anterior.numBaixas),
      sorter: (a, b) => a.atual.numBaixas - b.atual.numBaixas,
    },
    {
      title: 'Nº de vendas',
      render: (_, r) => r.estado.numVendaItens,
      sorter: (a, b) => a.estado.numVendaItens - b.estado.numVendaItens,
    },
    {
      title: 'Última venda',
      render: (_, r) => formatData(r.estado.dataUltimaVenda),
      sorter: (a, b) =>
        (a.estado.dataUltimaVenda ?? '').localeCompare(
          b.estado.dataUltimaVenda ?? ''
        ),
    },
    {
      title: 'Health',
      render: (_, r) => formatHealth(r.estado.healthScore),
      sorter: (a, b) =>
        (a.estado.healthScore ?? -1) - (b.estado.healthScore ?? -1),
    },
    {
      title: 'Vínculos (A/V)',
      render: (_, r) =>
        `${r.vinculos.adminsAtivos}/${r.vinculos.vendedoresAtivos}`,
      sorter: (a, b) =>
        a.vinculos.vendedoresAtivos - b.vinculos.vendedoresAtivos,
    },
    {
      title: 'Usuários ativos',
      render: (_, r) => r.vinculos.usuariosAtivosPeriodo,
      sorter: (a, b) =>
        a.vinculos.usuariosAtivosPeriodo - b.vinculos.usuariosAtivosPeriodo,
    },
    {
      title: 'Último login',
      render: (_, r) => formatDataHora(r.vinculos.ultimoLogin),
      sorter: (a, b) =>
        (a.vinculos.ultimoLogin ?? '').localeCompare(
          b.vinculos.ultimoLogin ?? ''
        ),
    },
    {
      title: 'Clientes',
      render: (_, r) => r.estado.numClientes,
      sorter: (a, b) => a.estado.numClientes - b.estado.numClientes,
    },
    {
      title: 'Atualizado em',
      render: (_, r) => formatDataHora(r.atualizadoEm),
      sorter: (a, b) =>
        (a.atualizadoEm ?? '').localeCompare(b.atualizadoEm ?? ''),
    },
  ]

  return (
    <Container>
      <ToastContainer theme="dark" />
      <ContainerTitle>
        <DivTitle color={colors.primaryText} size={24}>
          Gestão de Tenants
        </DivTitle>
      </ContainerTitle>

      <FilterBar>
        <FilterField>
          <span>De</span>
          <DatesPicker
            size="150px"
            background
            handleDataSelecionada={onChangeDataInicio}
          />
        </FilterField>
        <FilterField>
          <span>Até</span>
          <DatesPicker
            size="150px"
            background
            handleDataSelecionada={onChangeDataFim}
          />
        </FilterField>
        <FilterField>
          <span>Fases</span>
          <Select
            mode="multiple"
            allowClear
            style={{ minWidth: 280 }}
            placeholder="Em funcionamento"
            value={fases}
            onChange={value => setFases(value as FaseTenant[])}
            options={FASE_OPTIONS}
          />
        </FilterField>
        <FilterField>
          <span>Status da base</span>
          <Select
            style={{ width: 150 }}
            value={statusBase}
            onChange={value => setStatusBase(value as StatusBase)}
            options={STATUS_BASE_OPTIONS}
          />
        </FilterField>
      </FilterBar>

      <HeaderBar>
        <Carimbo>
          Atualizado em: {formatDataHora(ranking?.atualizadoEmGlobal ?? null)}
        </Carimbo>
        <Button type="primary" loading={recalculando} onClick={recalcular}>
          {recalculando ? 'Recalculando…' : 'Recalcular agora'}
        </Button>
      </HeaderBar>

      {isLoading ? (
        <Loading contrast={false} />
      ) : (
        <>
          {/* Desktop: tabela ordenável (com scroll horizontal em telas médias). */}
          <DesktopTabela>
            <TableWrapper>
              <Table
                rowKey="tenantId"
                columns={columns}
                dataSource={tenants}
                pagination={false}
                scroll={{ x: true }}
                locale={{ emptyText: 'Nenhum tenant no filtro selecionado.' }}
                onRow={record => ({
                  onClick: () =>
                    navigate(`/adm-gestao-tenants/${record.tenantId}`),
                })}
              />
            </TableWrapper>
          </DesktopTabela>

          {/* Telas estreitas: um card por tenant (a tabela não é responsiva). */}
          <MobileCards>
            {tenants.length === 0 ? (
              <Carimbo>Nenhum tenant no filtro selecionado.</Carimbo>
            ) : (
              tenants.map(t => (
                <TenantCard
                  key={t.tenantId}
                  onClick={() => navigate(`/adm-gestao-tenants/${t.tenantId}`)}
                >
                  <CardHeader>
                    <strong>{t.nome}</strong>
                    <Tag>{FASE_LABELS[t.fase]}</Tag>
                  </CardHeader>
                  <CardMetrics>
                    {cardMetrics(t).map(m => (
                      <MetricItem key={m.label}>
                        <span>{m.label}</span>
                        <span>{m.value}</span>
                      </MetricItem>
                    ))}
                  </CardMetrics>
                </TenantCard>
              ))
            )}
          </MobileCards>
        </>
      )}
    </Container>
  )
}
